import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FiArrowLeft, FiArrowRight, FiCalendar, FiClock } from 'react-icons/fi'
import { getRelatedPosts, getPostBySlug } from '../data/blogPosts'
import useBlogTracking, { trackBlogCta } from '../hooks/useBlogTracking'
import NewsletterInline from './NewsletterInline'

const SITE_URL = 'https://presenzia.app'

// Layout común para todos los artículos del blog. Encapsula nav, breadcrumb,
// header de artículo, índice (TOC), CTA final y enlaces a posts relacionados.
//
// Props:
// - slug: identificador del post.
// - post: opcional. Si se pasa, se usan sus campos {title, description, date,
//         dateDisplay, category, readMinutes, excerpt} en lugar de buscar en
//         data/blogPosts.js. Útil cuando el post viene de /api/blog/posts/:slug.
// - toc: array de { id, label } para el índice. Si vacío, se omite el bloque.
// - faqs: array de { question, answer } para schema FAQPage.
// - related: opcional. Lista de posts relacionados ya resueltos (de la API).
//            Si no se pasa, se calcula con getRelatedPosts() sobre data local.
// - children: contenido del artículo.
export default function BlogLayout({ slug, post: postProp, toc = [], faqs = [], related: relatedProp, children }) {
  const post = postProp || getPostBySlug(slug) || {}
  const url = `${SITE_URL}/blog/${slug}`
  const related = relatedProp || getRelatedPosts(slug, 3)

  useBlogTracking({ slug, category: post.category, readMinutes: post.readMinutes })

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.description,
    author: { '@type': 'Organization', name: 'PresenzIA', url: SITE_URL },
    publisher: { '@type': 'Organization', name: 'PresenzIA', url: SITE_URL },
    datePublished: post.date,
    dateModified: post.date,
    mainEntityOfPage: url,
    inLanguage: 'es',
    image: `${SITE_URL}/og-image.png`,
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: `${SITE_URL}/landing` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      { '@type': 'ListItem', position: 3, name: post.title, item: url },
    ],
  }

  const faqSchema = faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  } : null

  return (
    <>
      <Helmet>
        <title>{post.title} | PresenzIA</title>
        <meta name="description" content={post.description} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="PresenzIA" />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:section" content={post.category} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.description} />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Nav */}
        <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
            <Link to="/landing" className="flex items-center gap-2">
              <img src="/logo-presenzia-icon.svg" alt="PresenzIA" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-bold text-gray-900">Presenz<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">IA</span></span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/blog" className="text-sm font-medium text-gray-600 hover:text-gray-900 hidden sm:inline">Blog</Link>
              <Link
                to="/registro"
                onClick={() => trackBlogCta(slug, 'header_post')}
                className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Prueba gratis <FiArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </nav>

        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 transition-colors">
            <FiArrowLeft className="h-3.5 w-3.5" /> Volver al blog
          </Link>
        </div>

        {/* Article */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Header */}
          <header className="mb-10">
            <div className="flex items-center gap-2 text-sm text-blue-600 font-medium mb-3 flex-wrap">
              <FiCalendar className="h-4 w-4" />
              <span>{post.category}</span>
              <span className="text-gray-300">·</span>
              <time dateTime={post.date}>{post.dateDisplay}</time>
              {post.readMinutes && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="inline-flex items-center gap-1 text-gray-500">
                    <FiClock className="h-3.5 w-3.5" /> {post.readMinutes} min de lectura
                  </span>
                </>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
              {post.title}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
              {post.excerpt}
            </p>
          </header>

          {/* Table of contents */}
          {toc.length > 0 && (
            <nav className="bg-gray-50 rounded-2xl p-6 mb-10 border border-gray-100">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">Índice</h2>
              <ol className="space-y-2 text-sm text-blue-600">
                {toc.map((item, idx) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`} className="hover:underline">{idx + 1}. {item.label}</a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-p:leading-relaxed prose-a:text-blue-600 prose-strong:text-gray-900">
            {children}
          </div>

          {/* Newsletter inline (capture suave antes del CTA comercial) */}
          <NewsletterInline slug={slug} variant="card" />

          {/* CTA final */}
          <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl p-8 text-center my-12">
            <h3 className="text-2xl font-bold text-white mb-2">Cumple la ley en 5 minutos</h3>
            <p className="text-blue-100 mb-6">Prueba PresenzIA gratis 14 días. Sin tarjeta, sin compromiso.</p>
            <Link
              to="/registro"
              onClick={() => trackBlogCta(slug, 'cta_final_post')}
              className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold py-3 px-8 rounded-xl text-base shadow-xl hover:bg-blue-50 transition-all"
            >
              Empezar gratis <FiArrowRight className="h-4 w-4" />
            </Link>
            <p className="text-blue-200 text-xs mt-3">Desde 1,50 €/empleado/mes. Sin mínimos.</p>
          </div>

          {/* Related posts */}
          {related.length > 0 && (
            <section className="border-t border-gray-100 pt-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Sigue leyendo</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    onClick={() => trackBlogCta(slug, 'sigue_leyendo', { destino: p.slug })}
                    className="group block border border-gray-100 rounded-2xl p-5 hover:border-blue-200 hover:shadow-md transition-all"
                  >
                    <div className="text-xs font-medium text-blue-600 mb-2">{p.category}</div>
                    <h3 className="font-bold text-gray-900 group-hover:text-blue-700 mb-2 leading-snug">
                      {p.title}
                    </h3>
                    <p className="text-sm text-gray-500 line-clamp-3">{p.excerpt}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>

        {/* Footer */}
        <footer className="bg-gray-50 border-t border-gray-100 py-8">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <Link to="/landing" className="inline-flex items-center gap-2 mb-4">
              <img src="/logo-presenzia-icon.svg" alt="PresenzIA" className="w-6 h-6 rounded" />
              <span className="text-sm font-bold text-gray-900">Presenz<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">IA</span></span>
            </Link>
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mb-3 flex-wrap">
              <Link to="/privacidad" className="hover:text-gray-900">Privacidad</Link>
              <Link to="/aviso-legal" className="hover:text-gray-900">Aviso legal</Link>
              <Link to="/cookies" className="hover:text-gray-900">Cookies</Link>
              <Link to="/blog" className="hover:text-gray-900">Blog</Link>
            </div>
            <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} PresenzIA. Todos los derechos reservados.</p>
          </div>
        </footer>
      </div>
    </>
  )
}
